import React, { useState } from 'react';

import {
  SchemaPartyDetailsResponse,
  SchemaUserDeclaration,
} from '../../api/api-schema';
import { CollectionState, ElectionType } from '../../api/api-types';
import { fetcher } from '../../api/fetcher';
import Modal from '../../components/Modal/Modal';
import RichText from '../../components/RichText/RichText';
import { getErrorWithState } from '../../components/StatusMessages/StatusMessageOverlay';
import { useDictionary } from '../../hooks/useDictionary';
import {
  collectionStateByElectionType,
  getUnavailableActionState,
  GuardedDeclarationAction,
} from '../../utils/declarationActionState';
import { redirectToUrl } from '../../utils/browser-redirect';

type GuardedActionTarget = {
  action?: GuardedDeclarationAction;
  electionType?: ElectionType;
  partyId?: string;
};

type GuardedDialogActionProps = {
  action: GuardedDeclarationAction;
  children: (props: {
    isValidating: boolean;
    onClick: (event: React.MouseEvent) => void;
  }) => React.ReactNode;
  href?: string;
  onUnavailableAction?: OnUnavailableAction;
  targets: GuardedActionTarget[];
  userDeclarations?: SchemaUserDeclaration[] | null;
};

export type UnavailableAction = {
  action: GuardedDeclarationAction;
  state: CollectionState;
};

export type OnUnavailableAction = (action: UnavailableAction) => void;

export function UnavailableActionModal({
  action,
  onDismiss,
}: {
  action?: UnavailableAction;
  onDismiss: () => void;
}) {
  const { t } = useDictionary();

  return (
    <Modal
      isOpen={action !== undefined}
      onDismiss={onDismiss}
      title={t('errors.generic_title', { asString: true })}
    >
      <RichText>
        {t(
          getErrorWithState(
            action?.state,
            action?.action,
            'errors.generic_text',
          ),
        )}
      </RichText>
    </Modal>
  );
}

const isModifiedClick = (event: React.MouseEvent) =>
  event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

async function fetchPartyDetails(partyId: string) {
  const result = await fetcher<SchemaPartyDetailsResponse>(
    `/api/v1/parties/${partyId}`,
  );

  if (result.serviceResult === 0) {
    return undefined;
  }

  return result;
}

function getTargetDeclaration(
  target: GuardedActionTarget,
  userDeclarations?: SchemaUserDeclaration[] | null,
) {
  return userDeclarations?.find(
    (declaration) =>
      declaration.partyId === target.partyId &&
      declaration.electionType === target.electionType,
  );
}

function GuardedDialogAction({
  action,
  children,
  href,
  onUnavailableAction,
  targets,
  userDeclarations,
}: GuardedDialogActionProps) {
  const [isValidating, setValidating] = useState(false);
  const [unavailableAction, setUnavailableAction] = useState<
    UnavailableAction | undefined
  >();

  const onClick = async (event: React.MouseEvent) => {
    if (isValidating) {
      event.preventDefault();
      return;
    }

    if (
      !href ||
      event.defaultPrevented ||
      event.button !== 0 ||
      isModifiedClick(event)
    ) {
      return;
    }

    const validatedTargets = targets.filter(
      (target) =>
        !!target.partyId &&
        target.electionType !== undefined &&
        target.electionType !== ElectionType.BothTypes,
    );

    if (validatedTargets.length === 0) return;

    event.preventDefault();
    setValidating(true);

    try {
      for (const target of validatedTargets) {
        const partyDetails = await fetchPartyDetails(target.partyId!);
        const collectionState = partyDetails
          ? collectionStateByElectionType(partyDetails, target.electionType)
          : CollectionState.noCollection;
        const targetAction = target.action ?? action;
        const nextUnavailableState = getUnavailableActionState(
          targetAction,
          getTargetDeclaration(target, userDeclarations),
          collectionState,
        );

        if (nextUnavailableState !== undefined) {
          const nextUnavailableAction = {
            action: targetAction,
            state: nextUnavailableState,
          };

          if (onUnavailableAction) {
            onUnavailableAction(nextUnavailableAction);
          } else {
            setUnavailableAction(nextUnavailableAction);
          }
          return;
        }
      }

      redirectToUrl(href);
    } catch (error) {
      redirectToUrl(href);
    } finally {
      setValidating(false);
    }
  };

  return (
    <>
      {children({ isValidating, onClick })}
      <UnavailableActionModal
        action={unavailableAction}
        onDismiss={() => setUnavailableAction(undefined)}
      />
    </>
  );
}

export default GuardedDialogAction;
